import { formatLocationTime } from "@/lib/location-time";
import { Form, Head, usePage } from "@inertiajs/react";
import { useTranslation } from "react-i18next";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Spinner } from "@/components/ui/spinner";
import SettingsLayout from "@/layouts/settings_layout";
import { userBillingCheckoutPath, userBillingPortalPath } from "@/routes";

type Plan = {
  key: string;
  name: string;
  description: string;
  features: string[];
  limits: { users: number; locations: number | null };
  prices: Record<
    "monthly" | "yearly",
    { lookup_key: string; amount_cents: number }
  >;
};
type BillingAccount = {
  stripe_subscription_id?: string | null;
  stripe_trial_ends_at?: string | null;
  stripe_billing_interval?: "monthly" | "yearly" | null;
  stripe_subscription_status?: string | null;
  stripe_plan_key?: string | null;
  stripe_current_period_end?: string | null;
  stripe_cancel_at_period_end?: boolean;
};
type Invoice = {
  id: string;
  number?: string | null;
  total?: number;
  currency?: string;
  status?: string;
  hosted_invoice_url?: string | null;
  created_at?: string;
};

export default function BillingSettings({
  account,
  access,
  plans,
  currency,
  trial,
  invoices,
  invoice_history_state,
  stripe_configured: stripeConfigured,
}: {
  access: { allowed: boolean; trial_eligible: boolean; recoverable: boolean };
  account: BillingAccount;
  plans: Plan[];
  currency: string;
  trial: { days: number; plan_key: string; post_trial_lookup_key: string };
  invoices: Invoice[];
  invoice_history_state: "available" | "unavailable";
  stripe_configured: boolean;
}) {
  const { t, i18n } = useTranslation();
  const page = usePage().props;
  const isAdmin = page.auth.user?.role === "admin";
  const money = (amount: number, displayCurrency = currency) =>
    new Intl.NumberFormat(i18n.language, {
      style: "currency",
      currency: displayCurrency.toUpperCase(),
      currencyDisplay: "code",
    }).format(amount / 100);
  const future = plans
    .flatMap((plan) =>
      Object.values(plan.prices).map((price) => ({ ...price, plan })),
    )
    .find((price) => price.lookup_key === trial.post_trial_lookup_key)!;
  const interval =
    (!access.trial_eligible && account.stripe_billing_interval) ||
    (future.lookup_key.endsWith("yearly") ? "yearly" : "monthly");
  const current = plans.find((plan) => plan.key === account.stripe_plan_key);
  const terms =
    !access.trial_eligible && current
      ? { plan: current, amount_cents: current.prices[interval].amount_cents }
      : future;
  const date = (value: string) =>
    formatLocationTime(value, page.i18n, { dateStyle: "long" });
  return (
    <SettingsLayout>
      <Head title={t("navigation.subscription")} />
      <div className="space-y-6">
        <header className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-xl font-semibold">
              {t("navigation.subscription")}
            </h1>
            <p className="text-muted-foreground text-sm">
              {t("settings.billing_description")}
            </p>
          </div>
          {account.stripe_subscription_status && (
            <Badge>
              {t(
                !access.allowed &&
                  account.stripe_subscription_status === "active"
                  ? "recovery.expired"
                  : `billing_ui.statuses.${account.stripe_subscription_status}`,
              )}
            </Badge>
          )}
        </header>
        <Card>
          <CardContent className="space-y-3 pt-6">
            {!stripeConfigured && (
              <p role="status">{t("billing_ui.unavailable")}</p>
            )}
            <p>
              {t(
                access.trial_eligible ||
                  account.stripe_subscription_status === "trialing"
                  ? "billing_ui.future_terms"
                  : "recovery.paid_terms",
                {
                  plan: t(`billing_ui.plans.${terms.plan.key}.name`),
                  amount: money(terms.amount_cents),
                  interval: t(`billing_ui.${interval}`),
                },
              )}
            </p>
            {(access.trial_eligible ||
              account.stripe_subscription_status === "trialing") && (
              <p>
                {t("billing_ui.trial_features", {
                  plan: t(`billing_ui.plans.${trial.plan_key}.name`),
                })}
              </p>
            )}
            {account.stripe_trial_ends_at &&
              account.stripe_subscription_status === "trialing" && (
                <p>
                  {t("billing_ui.trial_end", {
                    date: date(account.stripe_trial_ends_at),
                  })}
                </p>
              )}
            {account.stripe_current_period_end && (
              <p>
                {t(
                  access.allowed
                    ? "billing_ui.next_bill"
                    : "recovery.term_ended",
                  {
                    date: date(account.stripe_current_period_end),
                  },
                )}
              </p>
            )}
            {account.stripe_cancel_at_period_end && (
              <p>{t("billing_ui.cancels")}</p>
            )}
            {isAdmin && !access.recoverable && (
              <Form action={userBillingCheckoutPath()} method="post">
                {({ processing }) => (
                  <Button
                    type="submit"
                    disabled={processing || !stripeConfigured}
                  >
                    {processing && <Spinner />}
                    {t(
                      access.trial_eligible
                        ? "billing_ui.start_trial"
                        : "recovery.subscribe",
                      { days: trial.days },
                    )}
                  </Button>
                )}
              </Form>
            )}
          </CardContent>
        </Card>
        <div className="grid gap-4 md:grid-cols-3">
          {plans.map((plan) => (
            <Card
              key={plan.key}
              className={
                account.stripe_plan_key === plan.key ? "border-primary" : ""
              }
            >
              <CardHeader>
                <CardTitle>
                  {t(`billing_ui.plans.${plan.key}.name`, {
                    defaultValue: plan.name,
                  })}
                </CardTitle>
                <CardDescription>
                  {t(`billing_ui.plans.${plan.key}.description`, {
                    defaultValue: plan.description,
                  })}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-2xl font-bold">
                  {money(plan.prices.monthly.amount_cents)}
                  <span className="text-muted-foreground text-sm font-normal">
                    {" "}
                    / {t("billing_ui.month")}
                  </span>
                </p>
                <p className="text-muted-foreground text-sm">
                  {t("billing_ui.annual_total", {
                    amount: money(plan.prices.yearly.amount_cents),
                  })}
                </p>
                <ul className="text-muted-foreground space-y-1 text-sm">
                  <li>
                    {t("billing_ui.staff_limit", { count: plan.limits.users })}
                  </li>
                  <li>
                    {plan.limits.locations === null
                      ? t("billing_ui.unlimited_locations")
                      : t("billing_ui.location_limit", {
                          count: plan.limits.locations,
                        })}
                  </li>
                  {plan.features.map((feature) => (
                    <li key={feature}>
                      {t(`billing_ui.features.${feature}`, {
                        defaultValue: feature.replace(/_/g, " "),
                      })}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          ))}
        </div>
        {isAdmin && access.recoverable && (
          <Form action={userBillingPortalPath()} method="post">
            {({ processing }) => (
              <Button
                type="submit"
                variant="outline"
                disabled={processing || !stripeConfigured}
              >
                {processing && <Spinner />}
                {t("billing_ui.open_portal")}
              </Button>
            )}
          </Form>
        )}
        <Card>
          <CardHeader>
            <CardTitle>{t("billing_ui.invoices")}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="divide-y">
              {invoices.map((invoice) => (
                <div
                  key={invoice.id}
                  className="flex items-center justify-between py-3"
                >
                  <div>
                    <p className="font-medium">
                      {invoice.number || invoice.id}
                    </p>
                    <p className="text-muted-foreground text-sm">
                      {t(`billing_ui.statuses.${invoice.status}`)}
                    </p>
                  </div>
                  {invoice.hosted_invoice_url ? (
                    <a
                      className="text-primary underline"
                      href={invoice.hosted_invoice_url}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {money(invoice.total || 0, invoice.currency)}
                    </a>
                  ) : (
                    <span>{money(invoice.total || 0, invoice.currency)}</span>
                  )}
                </div>
              ))}
              {invoice_history_state === "unavailable" && (
                <p role="status" className="py-4 text-sm text-muted-foreground">
                  {t("access.invoices_unavailable")}
                </p>
              )}
              {invoice_history_state === "available" &&
                invoices.length === 0 && (
                  <p className="text-muted-foreground py-4">
                    {t("billing_ui.no_invoices")}
                  </p>
                )}
            </div>
          </CardContent>
        </Card>
      </div>
    </SettingsLayout>
  );
}
